import React, { useEffect, useMemo, useRef, useState } from 'react';
import { reaction } from 'mobx';

import { useParams } from 'react-router-dom';
import { Tabs, Tab, Form, Button, Alert, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment-timezone';
import { fetchCustomerNodeDefinitionByZoneAndName } from '../../../../redux/slices/customer_node_definitionsSlice';
import { useTranslation } from 'react-i18next';
import { submitNode } from '../../../../redux/slices/nodeSubmitSlice';
import api from 'services/api';
import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import { useAppSelector, useAppDispatch } from 'redux/store';
import { selectHour12Option } from 'redux/selectors/userSettings';
import { fetchSchedulingWindows } from '../../../../redux/slices/scheduling_windowsSlice';
import { fetchRecurrenceDefinitions } from '../../../../redux/slices/recurrenceDefinitionsSlice';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { resolveRecurrenceDefinitionForSubmit } from 'api/recurrence_definitions/operations/resolveRecurrenceDefinitionForSubmit';
import { EditRecurrenceDefinitionReference, type RecurrenceDefinitionReference } from 'edit/reference/EditRecurrenceDefinitionReference';
import { RecurrenceRuleFields } from 'pages/apps/platform/recurrence_definitions/edit/RecurrenceRuleFields';
import { buildManualSnapshot, createManualDefinition, manualRule, manualWireRecurrences, seedManualFromSchedule } from './manualRecurrence';
import { EditSchedulingWindowReference, type SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import OpeningPeriodsButton from './OpeningPeriodsButton';
import MaskedDateTimeField from 'components/forms/MaskedDateTimeField';
import { mapAvailablePeriods } from 'api/recurrence/operations/mapAvailablePeriods';
import { mergeAvailablePeriods } from 'api/recurrence/operations/mergeAvailablePeriods';
import { computeFirstOccurrence } from 'api/recurrence/operations/computeFirstOccurrence';

// The recurrence schedule no longer lives in `submitBody`. A manual ad-hoc
// schedule is a single MobX `EditableRecurrence` rule (see `manualRecurrence`);
// a picked saved definition is submitted by id (`recurrence_definition_id`).
// `handleSubmit` assembles whichever of `schedule` / `recurrence_definition_id`
// applies onto the request body.
const INITIAL_SUBMIT_BODY = {
  name: '',
  queue_path: '',
  scheduled_start_datetime: moment().format(),
  scheduled_start_timezone: moment.tz.guess(),
  parameters: [],
  scheduling_window: '',
  hold_on_submit: false,
  note: '',
  start_node_id: null,
};

const normalizeKeyValuePairs = (val: unknown) => {
  if (typeof val !== 'string') {
    return val as any;
  }
  const i = val.indexOf('=');
  return (i >= 0 ? val.slice(0, i) : val).trim();
};

const getDropdownOptions = (optionsStr: string) =>
  (optionsStr || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseKeyValueOptions = (s: string) =>
  (s || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((pair) => {
      const [key, ...rest] = pair.split('=');
      const label = rest.join('=').trim();
      return { key: (key ?? '').trim(), label };
    })
    .filter(({ key, label }) => key && label);

const normalizeParamType = (t: any) =>
  String(t ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();

const isOutputOnlyParameter = (param: any) => param?.inout === 'out';

const parseDateTimeZoneValue = (raw: any) => {
  const str = String(raw ?? '').trim();
  if (!str) {
    return { datetime: '', timezone: moment.tz.guess() };
  }

  // format: YYYY-MM-DDTHH:mm:ss Europe/Amsterdam
  const spaceIndex = str.lastIndexOf(' ');
  if (spaceIndex > 0) {
    const dt = str.slice(0, spaceIndex).trim();
    const tz = str.slice(spaceIndex + 1).trim();
    return { datetime: dt, timezone: tz || moment.tz.guess() };
  }

  // fallback: treat everything as datetime
  return { datetime: str, timezone: moment.tz.guess() };
};

const NodeSubmit: React.FC = () => {
  const { zone_name, name, instance_id, node_definition_id } = useParams<{
    zone_name: string;
    name: string;
    instance_id: string;
    node_definition_id: string;
  }>();

  useEffect(() => {
    const prev = document.title;
    const base = instance_id ? `Submit Instance - ${instance_id} - ${name}` : `Submit Definition - ${node_definition_id} - ${name}`;

    document.title = base;
    return () => {
      document.title = prev;
    };
  }, [instance_id, node_definition_id]);

  const [selectedTab, setSelectedTab] = useState('submit');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // TODO #757 step 3 PR D: extend CustomerNodeDefinition in the slice with
  // child_nodes, parameters, default_queue_path so this cast goes away.
  const nodeDefinition = useAppSelector((state) =>
    state.customerNodeDefinitions.nodes.find((node: any) => node.zone_name === zone_name && node.name === name)
  ) as any;
  const queues = useAppSelector((state) => state.listOfQueues?.queues || []);
  const rootNodeInstanceId = useAppSelector((state) => state.nodeSubmit.rootNodeInstanceId);

  const [submitBody, setSubmitBody] = useState(INITIAL_SUBMIT_BODY);
  const [showAlert, setShowAlert] = useState(false);
  const schedulingWindows = useAppSelector((state) => state.schedulingWindow.items);

  const firstOccurrenceSeqRef = useRef(0);
  // Bumped when applying a calculated datetime so MaskedDateTimeField remounts
  // with the new value (its internal segments do not always sync from props).
  const [datetimePickerKey, setDatetimePickerKey] = useState(0);
  // Guards stale definition loads: picking B while A is still resolving must
  // not let A's late `setPickedRecurrences` clobber B's. Each pick (and the
  // clear-to-manual path) bumps this; a resolve only applies if it still owns
  // the latest sequence.
  const definitionLoadSeqRef = useRef(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userChangedDatetime, setUserChangedDatetime] = useState(false);
  const [suggestionMessage, setSuggestionMessage] = useState('');
  const [recurrenceMismatch, setRecurrenceMismatch] = useState<{
    message: string;
    suggestedValue: string;
  } | null>(null);
  const [suggestionError, setSuggestionError] = useState('');
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState<{ open: string; close: string }[]>([]);
  const [isWindowLoading, setIsWindowLoading] = useState(false);

  // Recurrence definitions
  const recurrenceDefinitions = useAppSelector((state) => state.recurrenceDefinitions.nodes);
  const hour12 = useAppSelector(selectHour12Option);
  // moment time token for user-facing displays; 12h preference → AM/PM,
  // otherwise 24h (also the 'auto' default for these moment-formatted strings).
  const displayTimeToken = hour12 ? 'h:mm A' : 'HH:mm';
  // The manual-rule reaction is set up once and captures the mount-time
  // `callFirstOccurrence`, which would otherwise close over a stale
  // `displayTimeToken`. Read the live token through a render-updated ref so a
  // 12h/24h preference change mid-session formats suggestions correctly.
  const displayTimeTokenRef = useRef(displayTimeToken);
  displayTimeTokenRef.current = displayTimeToken;
  const [selectedRecurrenceId, setSelectedRecurrenceId] = useState<string>('');
  // Manual ad-hoc schedule: a single editable recurrence rule (PM: no multiple
  // ad-hoc recurrences on submit). Stable for the component's lifetime.
  const manualDefinition = useMemo(createManualDefinition, []);
  // The recurrences of the currently picked saved definition, kept for the
  // first-occurrence preview (a definition can own several).
  const [pickedRecurrences, setPickedRecurrences] = useState<readonly Recurrence[]>([]);
  // Latest first-occurrence inputs, read by the manual-rule reaction (set up
  // once, so it must not close over stale React state). Refreshed each render.
  const firstOccurrenceInputsRef = useRef({
    timezone: '',
    datetime: '',
    schedulingWindow: '',
    userChangedDatetime: false,
    selectedRecurrenceId: '',
  });
  firstOccurrenceInputsRef.current = {
    timezone: submitBody.scheduled_start_timezone,
    datetime: submitBody.scheduled_start_datetime,
    schedulingWindow: submitBody.scheduling_window,
    userChangedDatetime,
    selectedRecurrenceId,
  };

  // Bridge between this dialog's id-string / wire-string state and the
  // object-based EditReference wrappers. Inflate to a partial T for
  // display; the wrappers' onChange handlers below deflate back. The
  // placeholder strings on inflate are only visible in the brief
  // moment before the LOV resolves.
  const recurrenceDefinitionValue: RecurrenceDefinitionReference | null = useMemo(() => {
    if (!selectedRecurrenceId) {
      return null;
    }
    const id = Number(selectedRecurrenceId);
    if (!Number.isFinite(id)) {
      return null;
    }
    return { recurrence_definition_id: id, name: '', zone_name: '' };
  }, [selectedRecurrenceId]);

  const schedulingWindowValue: SchedulingWindowReference | null = useMemo(() => {
    const s = (submitBody as { scheduling_window?: string }).scheduling_window;
    if (!s) {
      return null;
    }
    const [zone_name, name] = s.split('|');
    return name !== undefined ? { zone_name, name } : null;
  }, [submitBody]);
  const appliedHoldDefaultRef = useRef<string | null>(null);

  const [paramTimezones, setParamTimezones] = useState<Record<string, string>>({});

  const startNodeOptions = useMemo(() => {
    if (!Array.isArray(nodeDefinition?.child_nodes)) {
      return [];
    }
    return nodeDefinition.child_nodes.map((n: any) => ({
      label: `${n.node_definition_name || n.name || n.data?.label || 'Unknown'} (${n.type}) — ${n.id}`,
      value: n.id,
    }));
  }, [nodeDefinition]);

  const sortedParameters = useMemo(() => {
    if (!nodeDefinition?.parameters) {
      return [];
    }

    return [...nodeDefinition.parameters]
      .filter((param) => !isOutputOnlyParameter(param))
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }, [nodeDefinition?.parameters]);

  useEffect(() => {
    if (zone_name && name) {
      dispatch(
        fetchCustomerNodeDefinitionByZoneAndName({
          zone_name,
          node_definition_name: name,
        })
      );
    }
  }, [dispatch, zone_name, name]);

  const getNodeInstanceDetails = async () => {
    return api.get(`/node_instance_details/${instance_id}`);
  };

  const getNodeInstanceById = async () => {
    return api.get(`/node_instances/${instance_id}`);
  };

  useEffect(() => {
    if (instance_id) {
      const fetchData = async () => {
        try {
          const response = await getNodeInstanceDetails();
          const fullResponse = await getNodeInstanceById();

          if (response.data.node_instance.parameters) {
            setSubmitBody((prev) => ({
              ...prev,
              parameters: response.data.node_instance.parameters,
            }));
          }

          if (response.data.node_instance.queue_path) {
            setSubmitBody((prev) => ({
              ...prev,
              queue_path: response.data.node_instance.queue_path,
            }));
          }

          if (fullResponse.data.scheduling_window) {
            setSubmitBody((prev) => ({
              ...prev,
              scheduling_window: fullResponse.data.scheduling_window,
            }));
          }

          // A resubmit is a one-off rerun of this instance: it starts with no
          // recurrence so the operator can't accidentally register a second
          // scheduled chain (#952). The source instance's recurrence is left
          // untouched. The controls stay editable — the operator may still add
          // a recurrence deliberately, which submits as normal.

          if (fullResponse.data.scheduled_start_datetime) {
            setSubmitBody((prev) => ({
              ...prev,
              scheduled_start_datetime: fullResponse.data.scheduled_start_datetime,
            }));
          }

          if (fullResponse.data.scheduled_start_timezone) {
            setSubmitBody((prev) => ({
              ...prev,
              scheduled_start_timezone: fullResponse.data.scheduled_start_timezone,
            }));
          }
          if (fullResponse.data.start_node_id) {
            setSubmitBody((prev) => ({
              ...prev,
              start_node_id: fullResponse.data.start_node_id,
            }));
          }

          if (fullResponse.data.hold_on_submit !== undefined) {
            setSubmitBody((prev) => ({
              ...prev,
              hold_on_submit: !!fullResponse.data.hold_on_submit,
            }));
          }
        } catch (error) {
          console.error('Error fetching node instance details:', error);
        }
      };
      fetchData();
    }
  }, [instance_id]);

  useEffect(() => {
    dispatch(getQueuesPaginated({ page: 1, limit: 2000 }));
    dispatch(fetchSchedulingWindows({}));
    dispatch(fetchRecurrenceDefinitions({}));
  }, [dispatch]);

  // Recompute the first occurrence when the user edits the manual rule. The rule
  // is a MobX model mutated outside React, so a reaction bridges its changes
  // back to the (debounced) first-occurrence call; volatile inputs are read from
  // a ref to avoid a stale mount-time closure.
  useEffect(() => {
    const dispose = reaction(
      () => JSON.stringify(manualRule(manualDefinition).toSave()),
      () => {
        const inputs = firstOccurrenceInputsRef.current;
        if (inputs.selectedRecurrenceId) {
          return; // a saved definition is in effect; manual edits don't apply
        }
        const tz = inputs.timezone || moment.tz.guess();
        const nowStr = moment.tz(tz).format('YYYY-MM-DDTHH:mm:ss');
        setSubmitBody((prev) => ({ ...prev, scheduled_start_datetime: nowStr }));
        setUserChangedDatetime(false);
        setSuggestionMessage('');
        setRecurrenceMismatch(null);
        setSuggestionError('');
        setAvailablePeriods([]);
        callFirstOccurrence(manualWireRecurrences(manualDefinition), inputs.schedulingWindow, tz, nowStr, false, true);
      },
      { delay: 400 }
    );
    return dispose;
    // manualDefinition is stable. The reaction captures the mount-time
    // callFirstOccurrence; volatile inputs reach it through refs
    // (firstOccurrenceInputsRef, displayTimeTokenRef), not the stale closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!nodeDefinition || instance_id) {
      return;
    }
    const holdDefault = (nodeDefinition as any).hold_on_submit;
    if (holdDefault === undefined) {
      return;
    }

    // Apply definition default once per definition; don't overwrite later user toggles.
    const definitionKey = String(nodeDefinition.node_definition_id ?? '');
    if (appliedHoldDefaultRef.current === definitionKey) {
      return;
    }
    const holdOnSubmitDefault = Boolean(holdDefault);

    setSubmitBody((prev) => ({
      ...prev,
      hold_on_submit: holdOnSubmitDefault,
    }));
    appliedHoldDefaultRef.current = definitionKey;
  }, [nodeDefinition, instance_id]);

  const handleTabChange = (key: string | null) => {
    if (key) {
      setSelectedTab(key);
    }
  };

  // 🔹 Treat stored datetime as "naive" (ignore offset / Z) for display.
  const pickerValue = useMemo(() => {
    const raw = submitBody.scheduled_start_datetime;
    if (!raw) {
      return moment();
    }
    const withoutOffset = raw.replace(/([Zz]|[+-]\d{2}:\d{2})$/, '');
    return moment(withoutOffset);
  }, [submitBody.scheduled_start_datetime]);

  const { scheduled_start_datetime: rawDatetime, scheduled_start_timezone: tzPref } = submitBody;
  const datetimeTooltip = useMemo(() => {
    const tz = tzPref || moment.tz.guess();
    if (!rawDatetime) {
      return null;
    }
    const m = moment.tz(rawDatetime, tz);
    if (!m.isValid()) {
      return null;
    }
    const abbr = m.format('z');
    return `${m.format(`dddd, MMMM D, YYYY [at] ${displayTimeToken}`)} (${abbr})`;
  }, [rawDatetime, tzPref, displayTimeToken]);

  const handleDatetimeChange = (datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      return;
    }

    // 🔹 Keep wall-clock time as picked, store without timezone suffix
    const current = moment(submitBody.scheduled_start_datetime);

    const dateChanged = !datetime.isSame(current, 'day');
    const timeChanged = datetime.hour() !== current.hour() || datetime.minute() !== current.minute();

    if (!dateChanged && !timeChanged) {
      return;
    }

    const sanitized = datetime.clone().seconds(0).milliseconds(0);
    const newDatetimeStr = sanitized.format('YYYY-MM-DDTHH:mm:ss');

    setSubmitBody((prev) => ({
      ...prev,
      scheduled_start_datetime: newDatetimeStr,
    }));
    setUserChangedDatetime(true);
    setSuggestionMessage('');

    const tz = submitBody.scheduled_start_timezone || moment.tz.guess();

    if (submitBody.scheduling_window) {
      refreshSchedulingWindow(sanitized);
    }

    const recurrences = currentRecurrences();
    if (recurrences.length > 0 || submitBody.scheduling_window) {
      callFirstOccurrence(recurrences, submitBody.scheduling_window, tz, newDatetimeStr, true);
    }
  };

  const handleTimezoneChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = (e) => {
    const timezone = e.currentTarget.value;

    setSubmitBody((prev) => ({
      ...prev,
      scheduled_start_timezone: timezone,
    }));

    const recurrences = currentRecurrences();
    const hasWindow = !!submitBody.scheduling_window;

    if (recurrences.length > 0 || hasWindow) {
      const starttime = userChangedDatetime
        ? submitBody.scheduled_start_datetime || moment.tz(timezone).format('YYYY-MM-DDTHH:mm:ss')
        : moment.tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
      callFirstOccurrence(recurrences, submitBody.scheduling_window, timezone, starttime, userChangedDatetime);

      if (hasWindow) {
        const base = submitBody.scheduled_start_datetime
          ? submitBody.scheduled_start_datetime.replace(/([Zz]|[+-]\d{2}:\d{2})$/, '')
          : moment.tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
        refreshSchedulingWindow(moment(base).tz(timezone), submitBody.scheduling_window, timezone);
      }
    }
  };

  const handleQueueChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = (e) => {
    const queue_path = e.currentTarget.value;
    setSubmitBody((prev) => ({ ...prev, queue_path }));
  };

  // Pick a saved recurrence definition (submitted live by id), or clear back to
  // the manual ad-hoc rule. The definition's rules drive the first-occurrence
  // preview; on submit only its id is sent (see handleSubmit).
  const handleRecurrenceDefinitionChange = async (picked: RecurrenceDefinitionReference | null) => {
    const tz = submitBody.scheduled_start_timezone || moment.tz.guess();
    const nowStr = moment.tz(tz).format('YYYY-MM-DDTHH:mm:ss');
    // Invalidate any definition load still in flight; a stale resolve below
    // checks this before writing `pickedRecurrences` / `availablePeriods`.
    const loadSeq = ++definitionLoadSeqRef.current;

    if (picked === null) {
      setSelectedRecurrenceId('');
      setPickedRecurrences([]);
      seedManualFromSchedule(manualDefinition, null); // reset to a blank one-off
      setSubmitBody((prev) => ({ ...prev, scheduled_start_datetime: nowStr }));
      setSuggestionMessage('');
      setRecurrenceMismatch(null);
      setSuggestionError('');
      setUserChangedDatetime(false);
      setAvailablePeriods([]);
      if (submitBody.scheduling_window) {
        callFirstOccurrence([], submitBody.scheduling_window, tz, nowStr, false, true);
      }
      return;
    }

    const id = picked.recurrence_definition_id;
    setSelectedRecurrenceId(String(id));

    try {
      // Prefer the cached LOV row (it already carries recurrences); otherwise
      // the resolver loads the definition by id.
      const definition = await resolveRecurrenceDefinitionForSubmit(id, null, recurrenceDefinitions);
      if (loadSeq !== definitionLoadSeqRef.current) {
        return; // a newer pick (or clear) superseded this load
      }
      setPickedRecurrences(definition.recurrences);
      setAvailablePeriods([]);
      setSubmitBody((prev) => ({ ...prev, scheduled_start_datetime: nowStr }));
      setUserChangedDatetime(false);
      setSuggestionMessage('');
      setRecurrenceMismatch(null);
      setSuggestionError('');

      callFirstOccurrence(definition.recurrences, submitBody.scheduling_window, tz, nowStr, false, true);
    } catch (err: unknown) {
      if (loadSeq !== definitionLoadSeqRef.current) {
        return; // a newer pick (or clear) superseded this load
      }
      setPickedRecurrences([]);
      setSuggestionError((err as Error)?.message ?? 'Could not load the selected recurrence definition.');
    }
  };

  const refreshSchedulingWindow = async (baseMoment?: moment.Moment, selectedWindowKey?: string, selectedTimezone?: string) => {
    const selectedValue = selectedWindowKey ?? submitBody.scheduling_window;

    if (!selectedValue) {
      setAvailablePeriods([]);
      return;
    }

    setIsWindowLoading(true);

    const [zone, winName] = selectedValue.split('|');
    const win = schedulingWindows.find((w) => w.zone_name === zone && w.name === winName);
    if (!win) {
      setIsWindowLoading(false);
      return;
    }

    const tsMoment = baseMoment || moment(submitBody.scheduled_start_datetime).tz(submitBody.scheduled_start_timezone || moment.tz.guess());

    const selectedUtc = tsMoment.clone().utc();

    // ✅ Only reuse existing periods when we are NOT explicitly changing window
    if (availablePeriods.length > 0 && !selectedWindowKey) {
      let lastCloseUtc: moment.Moment | null = null;
      availablePeriods.forEach((p) => {
        const closeUtc = moment.utc(p.close);
        if (!lastCloseUtc || closeUtc.isAfter(lastCloseUtc)) {
          lastCloseUtc = closeUtc;
        }
      });

      if (lastCloseUtc && selectedUtc.isSameOrBefore(lastCloseUtc)) {
        setIsWindowLoading(false);
        return;
      }
    }

    try {
      const res = await api.post('/validate-scheduling-window', {
        scheduling_window_id: win.scheduling_window_id,
        timestamp: tsMoment.toISOString(),
        timezone: selectedTimezone ?? submitBody.scheduled_start_timezone,
      });

      // Keep original strings with offsets as returned from BE
      const periods = (res.data.available_periods || []).map((p: any) => ({
        open: p.open,
        close: p.close,
      }));

      setAvailablePeriods((prev) => (selectedWindowKey ? periods : mergeAvailablePeriods(prev, periods)));

      // periods are stored for calendar display only; datetime adjustment is
      // handled by callFirstOccurrence via the backend endpoint.
    } catch (err) {
      console.error('❌ Error loading available periods:', err);
      setAvailablePeriods([]);
    } finally {
      setIsWindowLoading(false);
    }
  };

  // The recurrence rules currently in effect: a picked saved definition's
  // rules, or the manual ad-hoc rule (empty until it's complete → a one-off).
  const currentRecurrences = (): readonly Recurrence[] => (selectedRecurrenceId ? pickedRecurrences : manualWireRecurrences(manualDefinition));

  /**
   * Calls the backend to compute the first occurrence given the current
   * schedule, window, and timezone. When `validate` is false (user has not
   * manually changed the datetime) the result replaces scheduled_start_datetime
   * and a suggestion message is shown. When `validate` is true (user owns the
   * datetime) the result is compared to `starttime` and a mismatch warning with
   * a "Use calculated value" button is shown instead.
   *
   * @param recurrences - Rules to evaluate (the manual rule, or the picked
   *   definition's rules); the earliest occurrence across them wins.
   * @param {string} schedulingWindow - Instance-level scheduling window key ("zone|name") or "".
   * @param {string} timezone - IANA timezone.
   * @param {string} starttime - ISO-like datetime string used as the reference.
   * @param {boolean} validate - When true, compare only; when false, auto-update.
   * @param {boolean} replacePeriods - When true, replace the eligible-period list;
   *   when false, merge new periods into the existing list (e.g. datetime navigation).
   */
  const callFirstOccurrence = async (
    recurrences: readonly Recurrence[],
    schedulingWindow: string,
    timezone: string,
    starttime: string,
    validate: boolean,
    replacePeriods = false
  ): Promise<void> => {
    const hasRecurrence = recurrences.length > 0;
    const hasWindow = !!schedulingWindow;
    if (!hasRecurrence && !hasWindow) {
      return;
    }

    const seq = ++firstOccurrenceSeqRef.current;

    try {
      const { earliest, windowEvaluation } = await computeFirstOccurrence({
        recurrences,
        schedulingWindow,
        timezone,
        starttime,
      });

      if (seq !== firstOccurrenceSeqRef.current) {
        return;
      }

      // Keep the window's available periods in sync with the occurrence calc:
      // the backend echoes the window evaluation on the response when a window
      // was queried (so they repopulate even right after a definition pick).
      if (hasWindow) {
        const incoming = [...mapAvailablePeriods(windowEvaluation)];
        setAvailablePeriods((prev) => (replacePeriods ? incoming : mergeAvailablePeriods(prev, incoming)));
      }

      if (earliest === null) {
        setSuggestionError('Received an invalid first-occurrence datetime.');
        setSuggestionMessage('');
        setRecurrenceMismatch(null);
        return;
      }
      const resultMoment = earliest;
      const tz = timezone || moment.tz.guess();
      const resultFormatted = resultMoment.format('YYYY-MM-DDTHH:mm:ss');

      const inputMoment = moment.tz(starttime, tz);
      // A valid result clears any prior error banner regardless of branch.
      setSuggestionError('');

      if (validate) {
        if (resultMoment.isSame(inputMoment, 'minute')) {
          setRecurrenceMismatch(null);
        } else {
          const displayTime = resultMoment.format(`YYYY-MM-DD ${displayTimeTokenRef.current} z`);
          setRecurrenceMismatch({
            message: `The selected datetime does not match the recurrence. Next occurrence: ${displayTime}.`,
            suggestedValue: resultFormatted,
          });
        }
      } else {
        setSubmitBody((prev) => ({
          ...prev,
          scheduled_start_datetime: resultFormatted,
        }));
        setRecurrenceMismatch(null);
        if (!resultMoment.isSame(inputMoment, 'minute')) {
          const displayTime = resultMoment.format(`YYYY-MM-DD ${displayTimeTokenRef.current} z`);
          setSuggestionMessage(`Scheduled start datetime has been set to the next occurrence: ${displayTime}.`);
        } else {
          setSuggestionMessage('');
        }
      }
    } catch (err: unknown) {
      if (seq !== firstOccurrenceSeqRef.current) {
        return;
      }
      const message = (err as any)?.response?.data?.error ?? (err as Error)?.message ?? 'Could not calculate first occurrence.';
      setSuggestionError(message);
      setSuggestionMessage('');
      setRecurrenceMismatch(null);
    }
  };

  const handleSchedulingWindowChange = async (picked: SchedulingWindowReference | null) => {
    if (picked === null) {
      const tz = submitBody.scheduled_start_timezone || moment.tz.guess();
      const nowStr = moment.tz(tz).format('YYYY-MM-DDTHH:mm:ss');
      setSubmitBody((prev) => ({
        ...prev,
        scheduling_window: '',
        scheduled_start_datetime: nowStr,
      }));
      setAvailablePeriods([]);
      setSuggestionMessage('');
      setRecurrenceMismatch(null);
      setSuggestionError('');
      setUserChangedDatetime(false);
      const recurrences = currentRecurrences();
      if (recurrences.length > 0) {
        callFirstOccurrence(recurrences, '', tz, nowStr, false);
      }
      return;
    }

    const selectedValue = `${picked.zone_name}|${picked.name}`;
    setSubmitBody((prev) => ({ ...prev, scheduling_window: selectedValue }));

    const tz = submitBody.scheduled_start_timezone || moment.tz.guess();
    const starttime = userChangedDatetime
      ? submitBody.scheduled_start_datetime || moment.tz(tz).format('YYYY-MM-DDTHH:mm:ss')
      : moment.tz(tz).format('YYYY-MM-DDTHH:mm:ss');

    callFirstOccurrence(currentRecurrences(), selectedValue, tz, starttime, userChangedDatetime, true);

    const currentMoment = moment(submitBody.scheduled_start_datetime).tz(tz);
    await refreshSchedulingWindow(currentMoment, selectedValue);
  };

  const handleParameterChange = (paramName: string, value: string) => {
    setSubmitBody((prev) => {
      const newParameters = [...prev.parameters];
      const def = nodeDefinition.parameters.find((p: any) => p.name === paramName);

      if (!def) {
        return prev;
      }

      const normalizedType = String(def.parameter_type || '')
        .replace(/\s+/g, '')
        .toLowerCase();
      const finalValue = normalizedType === 'keyvaluedropdown' ? normalizeKeyValuePairs(value) : value;

      const existingParamIndex = newParameters.findIndex((param) => param.name === paramName);

      if (existingParamIndex !== -1) {
        newParameters[existingParamIndex].value = finalValue;
      } else {
        newParameters.push({ name: paramName, value: finalValue });
      }

      return { ...prev, parameters: newParameters };
    });
  };

  const appliedDateDefaultsRef = useRef(false);
  useEffect(() => {
    if (instance_id) {
      return; // don't override instance-loaded values
    }
    if (!nodeDefinition?.parameters?.length) {
      return;
    }
    if (appliedDateDefaultsRef.current) {
      return;
    }

    const defs = nodeDefinition.parameters as any[];

    const defaultsToApply = defs
      .map((p, idx) => ({ p, idx }))
      .filter(({ p }) => {
        const t = normalizeParamType(p?.parameter_type);
        const isDate = t === 'date';
        const isDateTime = t === 'datetime';
        const isDateTimeZone = t === 'datetimezone';
        return (isDate || isDateTime || isDateTimeZone) && !!String(p?.default_expression ?? '').trim();
      });

    if (defaultsToApply.length === 0) {
      appliedDateDefaultsRef.current = true;
      return;
    }

    setSubmitBody((prev) => {
      const nextParams = [...(prev.parameters || [])];

      defaultsToApply.forEach(({ p, idx }) => {
        const name = p.name;
        const existingIdx = nextParams.findIndex((x: any) => x.name === name);
        if (existingIdx !== -1) {
          return; // already set
        }
        nextParams.push({ name, value: String(p.default_expression ?? '') });
      });

      return { ...prev, parameters: nextParams };
    });

    appliedDateDefaultsRef.current = true;
  }, [nodeDefinition, instance_id]);

  const handleDateParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    const sanitized = datetime.clone().seconds(0).milliseconds(0);
    handleParameterChange(paramName, sanitized.format('YYYY-MM-DD'));
  };

  const handleDateTimeParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    const sanitized = datetime.clone().seconds(0).milliseconds(0);
    handleParameterChange(paramName, sanitized.format('YYYY-MM-DDTHH:mm:ss'));
  };

  const handleDateTimeZoneParamChange = (paramName: string, datetime: moment.Moment | string) => {
    const def = nodeDefinition.parameters.find((p: any) => p.name === paramName);
    if (!def) {
      return;
    }

    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }

    const currentRaw = getParamValue(paramName);
    const parsed = parseDateTimeZoneValue(currentRaw);

    const tz = paramTimezones[paramName] || parsed.timezone || moment.tz.guess();
    const sanitized = datetime.clone().seconds(0).milliseconds(0);

    handleParameterChange(paramName, `${sanitized.format('YYYY-MM-DDTHH:mm:ss')} ${tz}`);
  };

  const handleTimeParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    handleParameterChange(paramName, datetime.format('HH:mm:ss'));
  };

  const handleDateTimeZoneParamTimezoneChange = (paramName: string, timezone: string) => {
    setParamTimezones((prev) => ({ ...prev, [paramName]: timezone }));

    const currentRaw = getParamValue(paramName);
    const parsed = parseDateTimeZoneValue(currentRaw);

    if (!parsed.datetime) {
      return;
    }

    handleParameterChange(paramName, `${parsed.datetime} ${timezone}`);
  };

  const handleStartNodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSubmitBody((prev) => ({
      ...prev,
      start_node_id: val === 'full' ? null : val,
    }));
  };

  // On submit, convert the stored "naive" time back into
  // an ISO string with the correct offset for scheduled_start_timezone.
  const handleSubmit = async () => {
    const nodeParamDefs = (nodeDefinition?.parameters || []).filter((paramDefinition: any) => !isOutputOnlyParameter(paramDefinition));

    // Build params from definitions, falling back to default_expression when the user didn't touch a field.
    const normalizedParams = (nodeParamDefs as any[])
      .map((def) => {
        const existing = (submitBody.parameters || []).find((p: any) => p.name === def.name);

        const hasExisting = existing && existing.value !== undefined && existing.value !== null && String(existing.value).trim() !== '';

        const defDefault = String(def?.default_expression ?? '').trim();
        const hasDefault = !!defDefault;

        // Keep prior behavior: don't send totally empty params
        if (!hasExisting && !hasDefault) {
          return null;
        }

        const normalizedType = normalizeParamType(def?.parameter_type);

        let value = hasExisting ? existing.value : defDefault;

        if (normalizedType === 'keyvaluedropdown') {
          value = normalizeKeyValuePairs(value);
        }

        return { name: def.name, value };
      })
      .filter(Boolean) as any[];

    const timezone = submitBody.scheduled_start_timezone || moment.tz.guess();

    let finalScheduled = submitBody.scheduled_start_datetime;
    if (finalScheduled) {
      // strip any trailing Z or offset and interpret as local time in `timezone`
      const base = finalScheduled.replace(/([Zz]|[+-]\d{2}:\d{2})$/, '');
      const local = moment.tz(base, timezone);
      // keep offset in the string
      finalScheduled = local.toISOString(true);
    }

    const bodyToSubmit: any = {
      ...submitBody,
      scheduled_start_datetime: finalScheduled,
      parameters: normalizedParams,
    };

    const trimmedNote = String(submitBody.note || '').trim();
    if (trimmedNote) {
      bodyToSubmit.note = trimmedNote;
    } else {
      delete bodyToSubmit.note;
    }

    // Schedule XOR recurrence_definition_id: a picked definition is submitted
    // live by id; a manual ad-hoc rule is frozen into a schedule snapshot
    // (omitted entirely for a one-off submit).
    delete bodyToSubmit.schedule;
    delete bodyToSubmit.recurrence_definition_id;
    if (selectedRecurrenceId) {
      bodyToSubmit.recurrence_definition_id = Number(selectedRecurrenceId);
    } else {
      const snapshot = buildManualSnapshot(manualDefinition);
      if (snapshot) {
        bodyToSubmit.schedule = snapshot;
      }
    }

    if (nodeDefinition?.version != null) {
      (bodyToSubmit as any).if_match_version = nodeDefinition.version;
    }

    if (instance_id) {
      bodyToSubmit.source_instance_id = Number(instance_id);
    }

    setIsSubmitting(true);

    try {
      const resultAction = await dispatch(
        submitNode({
          zone_name,
          name,
          data: bodyToSubmit,
        })
      );

      if (submitNode.fulfilled.match(resultAction)) {
        setShowAlert(true);
        setErrorMessage('');
        setSubmitBody((prev) => ({
          ...prev,
          note: '',
        }));
      } else if (submitNode.rejected.match(resultAction)) {
        const payload = resultAction.payload as { error: string } | undefined;
        const errorMessage = payload?.error || resultAction.error?.message || 'Submission failed.';
        setErrorMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting node:', error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 3000);
    }
  };

  const isDateValid = (current: moment.Moment) => {
    if (submitBody.scheduling_window === '' || availablePeriods.length === 0) {
      return true;
    }

    const timezone = submitBody.scheduled_start_timezone || moment.tz.guess();

    const currentLocal = current.clone().tz(timezone);
    const currentDateStr = currentLocal.format('YYYY-MM-DD');

    // Find the last close in LOCAL time
    let lastCloseLocal: moment.Moment | null = null;
    availablePeriods.forEach((p) => {
      const closeLocal = moment.utc(p.close).tz(timezone);
      if (!lastCloseLocal || closeLocal.isAfter(lastCloseLocal)) {
        lastCloseLocal = closeLocal;
      }
    });

    // After the last known close day → everything is allowed
    if (lastCloseLocal && currentLocal.isAfter(lastCloseLocal, 'day')) {
      return true;
    }

    // Up to and including the lastClose day → only OPEN dates are selectable
    return availablePeriods.some((p) => {
      const openLocal = moment.utc(p.open).tz(timezone);
      const openDateStr = openLocal.format('YYYY-MM-DD');
      return currentDateStr === openDateStr;
    });
  };

  const handleDatetimeBlur = () => {
    if (!submitBody.scheduling_window) {
      return;
    }

    const current = moment(submitBody.scheduled_start_datetime).tz(submitBody.scheduled_start_timezone || moment.tz.guess());

    refreshSchedulingWindow(current);
  };

  if (!nodeDefinition) {
    return <div>Loading...</div>;
  }

  const getParamValue = (paramName: string) => submitBody.parameters.find((p: any) => p.name === paramName)?.value ?? '';

  const isQueueSelectionMissing = !nodeDefinition.default_queue_path && submitBody.queue_path === '';
  const submitDisabledReason = (() => {
    if (isSubmitting) {
      return 'Submission in progress.';
    }
    if (isWindowLoading) {
      return 'Scheduling window is being checked.';
    }
    if (isQueueSelectionMissing) {
      return 'Select a queue before submitting.';
    }
    return null;
  })();
  const isSubmitDisabled = Boolean(submitDisabledReason);

  const renderSubmitButton = (tooltipId: string) => {
    if (submitDisabledReason) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={tooltipId} style={{ position: 'fixed' }}>
              {submitDisabledReason}
            </Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button disabled style={{ pointerEvents: 'none' }}>
              Submit
            </Button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <Button disabled={isSubmitDisabled} onClick={handleSubmit}>
        Submit
      </Button>
    );
  };

  const renderInitialNoteField = (controlId: string) => (
    <Form.Group controlId={controlId} className="mb-3">
      <Form.Label>Initial Note</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        placeholder="Optional note to attach to the created node instance"
        value={submitBody.note}
        onChange={(e) =>
          setSubmitBody((prev) => ({
            ...prev,
            note: e.target.value,
          }))
        }
      />
      <Form.Text className="text-muted">This note will be attached to the created node instance.</Form.Text>
    </Form.Group>
  );

  return (
    <div
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {showAlert && errorMessage === '' && (
        <Alert variant="outline-success" onClose={() => setShowAlert(false)} dismissible>
          Successfully submitted{' '}
          <a href={`/automation/node_instances?node_instance_id=${rootNodeInstanceId}`} target="_parent">
            View Node Instance
          </a>
        </Alert>
      )}
      {errorMessage && (
        <Alert variant="outline-danger" onClose={() => setShowAlert(false)} dismissible>
          {errorMessage}
        </Alert>
      )}
      {suggestionMessage && (
        <Alert variant="outline-info" onClose={() => setSuggestionMessage('')} dismissible>
          {suggestionMessage}
        </Alert>
      )}
      {recurrenceMismatch && (
        <Alert variant="outline-warning" onClose={() => setRecurrenceMismatch(null)} dismissible>
          {recurrenceMismatch.message}{' '}
          <Button
            variant="link"
            size="sm"
            className="p-0 align-baseline"
            onClick={() => {
              setSubmitBody((prev) => ({
                ...prev,
                scheduled_start_datetime: recurrenceMismatch.suggestedValue,
              }));
              setUserChangedDatetime(false);
              setRecurrenceMismatch(null);
              setDatetimePickerKey((k) => k + 1);
            }}
          >
            Use calculated value
          </Button>
        </Alert>
      )}
      {suggestionError && (
        <Alert variant="outline-danger" onClose={() => setSuggestionError('')} dismissible>
          Could not calculate first occurrence: {suggestionError}
        </Alert>
      )}
      <h3>Submit Node Definition: {name}</h3>
      <Tabs id="node-submit-tabs" activeKey={selectedTab} onSelect={handleTabChange} className="mb-2 border-bottom" variant="underline">
        <Tab eventKey="submit" title="Submit">
          <div>
            <Form.Group controlId="scheduled_start_datetime">
              <Form.Label>Scheduled Start Datetime</Form.Label>
              <OverlayTrigger placement="top" overlay={datetimeTooltip ? <Tooltip id="scheduled-start-datetime-tooltip">{datetimeTooltip}</Tooltip> : <></>}>
                <div>
                  <MaskedDateTimeField
                    key={`${submitBody.scheduling_window}-${datetimePickerKey}`}
                    value={pickerValue}
                    onChange={handleDatetimeChange}
                    onClose={handleDatetimeBlur}
                    mode="datetime"
                    placeholder="Type or select date and time"
                    isValidDate={submitBody.scheduling_window && availablePeriods.length > 0 ? isDateValid : undefined}
                  />
                </div>
              </OverlayTrigger>
            </Form.Group>

            <Form.Group controlId="scheduled_start_timezone">
              <Form.Label>Scheduled Start Timezone</Form.Label>
              <Form.Control as="select" value={submitBody.scheduled_start_timezone} onChange={handleTimezoneChange}>
                {moment.tz.names().map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="queue_name">
              <Form.Label>Queue</Form.Label>
              <Form.Control as="select" value={submitBody.queue_path ?? ''} onChange={handleQueueChange}>
                {instance_id ? (
                  <option value={submitBody.queue_path}>{submitBody.queue_path}</option>
                ) : (
                  <option value="">Default ({nodeDefinition.default_queue_path})</option>
                )}
                {queues.map((queue: any) => (
                  <option key={queue.queue_id} value={queue.zone_name + '|' + queue.queue_name}>
                    {queue.queue_name} ({queue.zone_name})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="schedule" className="mb-3">
              <Form.Label>Schedule</Form.Label>

              <div className="mb-2">
                <EditRecurrenceDefinitionReference value={recurrenceDefinitionValue} onChange={handleRecurrenceDefinitionChange} />
              </div>

              {/* Manual ad-hoc schedule: a single rule, edited with the same
                  control as the recurrence-definition editor. Hidden when a
                  saved definition is picked (it is used live by id). */}
              {!selectedRecurrenceId && (
                <Card className="mb-2">
                  <Card.Body>
                    <RecurrenceRuleFields recurrence={manualRule(manualDefinition)} hideDescription />
                    <Form.Check
                      type="checkbox"
                      className="mt-3"
                      label="Resubmit on error"
                      checked={manualDefinition.resubmitOnError}
                      onChange={(e) => manualDefinition.setResubmitOnError(e.target.checked)}
                    />
                  </Card.Body>
                </Card>
              )}

              <Form.Check
                type="checkbox"
                label="Hold on submit"
                name="hold_on_submit"
                checked={!!submitBody?.hold_on_submit}
                onChange={(e) =>
                  setSubmitBody((prev) => ({
                    ...prev,
                    hold_on_submit: e.target.checked,
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId="schedulingWindow" className="mb-3">
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <EditSchedulingWindowReference
                    value={schedulingWindowValue}
                    onChange={handleSchedulingWindowChange}
                    disabled={Number.isInteger(nodeDefinition?.restrict_to_scheduling_window)}
                  />
                </div>
                <OpeningPeriodsButton
                  periods={availablePeriods}
                  windowName={submitBody.scheduling_window}
                  disabled={!submitBody.scheduling_window || isWindowLoading || availablePeriods.length === 0}
                  timezone={submitBody.scheduled_start_timezone}
                />
              </div>
              {isWindowLoading && <div className="text-muted small mt-1">Checking scheduling window...</div>}
            </Form.Group>

            {Array.isArray(nodeDefinition.child_nodes) && nodeDefinition.child_nodes.length > 0 && (
              <Form.Group className="mb-3" controlId="start_node_id">
                <Form.Label className="mb-1">Start from</Form.Label>
                <Form.Select value={submitBody.start_node_id ?? 'full'} onChange={handleStartNodeChange}>
                  <option value="full">Run entire flow</option>
                  {startNodeOptions.map((n) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {renderInitialNoteField('submit_note_main')}
            {renderSubmitButton('submit-disabled-queue-tooltip-main')}
          </div>
        </Tab>

        <Tab eventKey="parameters" title="Parameters">
          <div>
            <Form.Check
              type="switch"
              id="show-descriptions-switch-submit"
              label="Show descriptions"
              checked={showDescriptions}
              onChange={(e) => setShowDescriptions(e.target.checked)}
              className="mb-3"
            />
            {sortedParameters.map((param: any) => {
              const normalizedType = normalizeParamType(param.parameter_type);
              const isDropdown = normalizedType === 'dropdown';
              const isKeyValueDropdown = normalizedType === 'keyvaluedropdown';
              const isDate = normalizedType === 'date';
              const isDateTime = normalizedType === 'datetime';
              const isDateTimeZone = normalizedType === 'datetimezone';
              const isTime = normalizedType === 'time';

              const dropdownOptions = isDropdown ? getDropdownOptions(param.options || '') : [];

              const kvOptions = isKeyValueDropdown ? parseKeyValueOptions(param.options || '') : [];

              const instValue = getParamValue(param.name);

              const rawCurrent =
                instValue !== undefined && instValue !== null && instValue !== ''
                  ? instValue
                  : isDate || isDateTime || isDateTimeZone
                    ? (param.default_expression ?? '')
                    : isDropdown || isKeyValueDropdown
                      ? (param.default_expression ?? '')
                      : (param.default_expression ?? '');

              const currentValue = isKeyValueDropdown ? normalizeKeyValuePairs(String(rawCurrent)) : rawCurrent;

              const displayLabel = showDescriptions && param.description ? param.description : param.name;

              const dateMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, 'YYYY-MM-DD', true);
                return m.isValid() ? m : undefined;
              })();

              const dateTimeMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, moment.ISO_8601, true);
                return m.isValid() ? m : undefined;
              })();

              const dateTimeZoneParts = (() => {
                const parsed = parseDateTimeZoneValue(currentValue);
                const tzFromState = paramTimezones[param.name] || parsed.timezone || moment.tz.guess();
                if (!parsed.datetime) {
                  return { momentValue: undefined, tzValue: tzFromState };
                }
                const m = moment(parsed.datetime, moment.ISO_8601, true);
                return {
                  momentValue: m.isValid() ? m : undefined,
                  tzValue: tzFromState,
                };
              })();

              const timeMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, 'HH:mm:ss', true);
                return m.isValid() ? m : undefined;
              })();

              const showClear = (isDate || isDateTime || isDateTimeZone || isTime) && String(currentValue ?? '').trim().length > 0;

              return (
                <Form.Group key={param.name} controlId={`param-${param.name}`} className="mb-3">
                  <Form.Label>{displayLabel}</Form.Label>

                  {isDropdown && (
                    <Form.Select
                      value={dropdownOptions.includes(String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    >
                      {dropdownOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        <>
                          <option value=""></option>
                          {dropdownOptions.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </>
                      )}
                    </Form.Select>
                  )}

                  {isKeyValueDropdown && (
                    <Form.Select
                      value={kvOptions.some((o) => o.key === String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    >
                      {kvOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        kvOptions.map(({ key, label }) => (
                          <option key={key} value={key}>
                            {label} ({key})
                          </option>
                        ))
                      )}
                    </Form.Select>
                  )}

                  {isDate && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={dateMoment ?? ''}
                          onChange={(m) => handleDateParamChange(param.name, m)}
                          mode="date"
                          placeholder="YYYY-MM-DD"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {isDateTime && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={dateTimeMoment ?? ''}
                          onChange={(m) => handleDateTimeParamChange(param.name, m)}
                          mode="datetime"
                          placeholder="YYYY-MM-DD HH:mm:ss"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {isDateTimeZone && (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <div style={{ flex: 1 }}>
                          <MaskedDateTimeField
                            value={dateTimeZoneParts.momentValue ?? ''}
                            onChange={(m) => handleDateTimeZoneParamChange(param.name, m)}
                            mode="datetime"
                            placeholder="YYYY-MM-DD HH:mm:ss"
                          />
                        </div>
                        <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                          Clear
                        </Button>
                      </div>
                      <Form.Control
                        as="select"
                        value={dateTimeZoneParts.tzValue}
                        onChange={(e) => handleDateTimeZoneParamTimezoneChange(param.name, e.currentTarget.value)}
                      >
                        {moment.tz.names().map((timezone) => (
                          <option key={timezone} value={timezone}>
                            {timezone}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  )}

                  {isTime && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={timeMoment ?? ''}
                          onChange={(m) => handleTimeParamChange(param.name, m)}
                          mode="time"
                          showCalendar={false}
                          placeholder="HH:mm:ss"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {!isDropdown && !isKeyValueDropdown && !isDate && !isDateTime && !isDateTimeZone && !isTime && (
                    <Form.Control
                      type="text"
                      placeholder={param.default_expression}
                      value={String(currentValue)}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    />
                  )}
                </Form.Group>
              );
            })}

            {renderInitialNoteField('submit_note_parameters')}
            {renderSubmitButton('submit-disabled-queue-tooltip-parameters')}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default NodeSubmit;
